'use client';

import { MagnifyingGlass } from '@phosphor-icons/react/ssr';
import { type ElementType, type ReactNode, useId, useState } from 'react';

import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from './button';
import { Checkbox } from './checkbox';
import { useFieldLabelId } from './field';
import { menuStyles } from './menu-styles';
import { SelectButton } from './select';
import { TextInput } from './text-input';

export type MultiSelectOption = { value: string; label: ReactNode; disabled?: boolean };

export type MultiSelectProps = {
  options: MultiSelectOption[];
  /** Applied selection. */
  value: string[];
  /** Called with the new selection when the user presses Apply. */
  onValueChange: (value: string[]) => void;
  placeholder?: ReactNode;
  /** Overline above the options, e.g. "Publish to". */
  header?: ReactNode;
  /** Show the search field. Defaults to on when there are more than 7 options (Figma). */
  searchable?: boolean;
  searchPlaceholder?: string;
  /** Trigger text for a selection. Defaults to the option label, or "3 selected". */
  formatValue?: (selected: MultiSelectOption[]) => ReactNode;
  size?: 'sm' | 'md' | 'lg';
  leadingIcon?: ElementType<{ className?: string }>;
  disabled?: boolean;
  id?: string;
  /** Needed when not inside a Field. */
  'aria-label'?: string;
  className?: string;
  /** Control the panel's open state (uncontrolled by default). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Start open when uncontrolled. */
  defaultOpen?: boolean;
};

/** Search matches the label when it is text, else the value. */
const searchText = (o: MultiSelectOption) =>
  typeof o.label === 'string' ? o.label : o.value;

const defaultFormat = (selected: MultiSelectOption[]) =>
  selected.length === 1
    ? selected[0]!.label
    : selected.length
      ? `${selected.length} selected`
      : '';

/**
 * Choose several options (Figma: Dropdown ↳ Menu, Type=Multi): a Select-style
 * trigger opening a panel with optional search, checkbox rows and a Clear /
 * Apply footer. Changes are a draft until Apply; Escape or clicking away
 * discards them. Built on Radix Popover - the panel is a labelled dialog with
 * a group of real checkboxes, which is what this layout is to assistive tech.
 */
export function MultiSelect({
  options,
  value,
  onValueChange,
  placeholder,
  header,
  searchable = options.length > 7,
  searchPlaceholder = 'Search',
  formatValue = defaultFormat,
  size,
  leadingIcon,
  disabled,
  id,
  'aria-label': ariaLabel,
  className,
  open: openProp,
  onOpenChange: onOpenChangeProp,
  defaultOpen = false,
}: MultiSelectProps) {
  const [openState, setOpenState] = useState(defaultOpen);
  const open = openProp ?? openState;
  const setOpen = (next: boolean) => {
    setOpenState(next);
    onOpenChangeProp?.(next);
  };
  const [draft, setDraft] = useState<string[]>(value);
  const [query, setQuery] = useState('');
  const headerId = useId();
  const fieldLabelId = useFieldLabelId();
  const menu = menuStyles();

  const selected = options.filter((o) => value.includes(o.value));
  const shown = query
    ? options.filter((o) => searchText(o).toLowerCase().includes(query.toLowerCase()))
    : options;

  const onOpenChange = (next: boolean) => {
    if (next) {
      setDraft(value);
      setQuery('');
    }
    setOpen(next);
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <SelectButton
          id={id}
          size={size}
          leadingIcon={leadingIcon}
          disabled={disabled}
          placeholder={placeholder}
          value={formatValue(selected)}
          aria-label={ariaLabel}
          aria-haspopup="dialog"
          className={className}
        />
      </PopoverTrigger>
      <PopoverContent
        aria-labelledby={fieldLabelId}
        aria-label={fieldLabelId ? undefined : ariaLabel}
        className="w-(--radix-popover-trigger-width)"
      >
        {searchable && (
          <div className={menu.search()}>
            <TextInput
              size="sm"
              leadingIcon={MagnifyingGlass}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        )}
        <div role="group" aria-labelledby={header ? headerId : undefined}>
          {header && (
            <div id={headerId} className={menu.header()}>
              {header}
            </div>
          )}
          {shown.map((option) => {
            const checked = draft.includes(option.value);
            return (
              <label
                key={option.value}
                data-slot="multi-select-option"
                data-state={checked ? 'checked' : undefined}
                data-disabled={option.disabled ? '' : undefined}
                className={menu.item({
                  className: 'hover:bg-bg-muted has-focus-visible:bg-bg-muted',
                })}
              >
                <Checkbox
                  checked={checked}
                  disabled={option.disabled}
                  onCheckedChange={(on) =>
                    setDraft((d) =>
                      on ? [...d, option.value] : d.filter((v) => v !== option.value),
                    )
                  }
                />
                <span className={menu.label()}>{option.label}</span>
              </label>
            );
          })}
        </div>
        <div role="separator" className={menu.divider()} />
        <div className={menu.footer()}>
          <Button size="sm" variant="ghost" onClick={() => setDraft([])}>
            Clear
          </Button>
          <Button
            size="sm"
            onClick={() => {
              onValueChange(draft);
              setOpen(false);
            }}
          >
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
